import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

import { useApiClient } from '../../hooks/useApiClient';
import type { AgentProvisionResponse, AgentSummary, AgentTokenSummary } from '../../types';

interface CreateAgentForm {
  displayName: string;
  externalIdentifier: string;
  notes: string;
  isActive: boolean;
}

const INITIAL_FORM: CreateAgentForm = {
  displayName: '',
  externalIdentifier: '',
  notes: '',
  isActive: true,
};

interface IssuedAgentToken {
  token: string;
  qrPayload: string;
}

const filenameSafe = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'token';

export default function AgentsPage() {
  const apiClient = useApiClient();
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateAgentForm>(INITIAL_FORM);
  const [issuedSecrets, setIssuedSecrets] = useState<Record<string, IssuedAgentToken>>({});
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [expandedTokenId, setExpandedTokenId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleDownloadQr = useCallback((tokenId: string, label: string, prefix: string) => {
    const canvas = document.getElementById(`agent-qr-${tokenId}`) as HTMLCanvasElement | null;
    if (!canvas) {
      toast.error('QR code not ready');
      return;
    }
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `agent-${filenameSafe(label)}-${prefix}.png`;
      link.click();
    } catch (error) {
      console.error(error);
      toast.error('Unable to download QR code');
    }
  }, []);

  const loadAgents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient('/api/admin/agents');
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const payload = (await response.json()) as AgentSummary[];
      setAgents(payload);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Unable to load agents from the backend.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  const sortedAgents = useMemo(
    () => [...agents].sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })),
    [agents],
  );

  const handleCreateAgent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.displayName.trim()) {
      toast.error('Display name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient('/api/admin/agents', {
        method: 'POST',
        body: JSON.stringify({
          displayName: form.displayName.trim(),
          externalIdentifier: form.externalIdentifier.trim() || undefined,
          notes: form.notes.trim() || undefined,
          isActive: form.isActive,
        }),
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || 'Unable to create agent');
      }
      const payload = (await response.json()) as AgentProvisionResponse;
      setAgents((prev) => [...prev, payload.agent]);
      setIssuedSecrets((prev) => ({
        ...prev,
        [payload.tokenInfo.id]: {
          token: payload.token,
          qrPayload: payload.tokenInfo.qrPayload ?? '',
        },
      }));
      setExpandedAgent(payload.agent.id);
      setExpandedTokenId(payload.tokenInfo.id);
      toast.success(`Added agent ${payload.agent.displayName}. Copy the token now—it's shown only once.`);
      setForm(INITIAL_FORM);
    } catch (submitError) {
      console.error(submitError);
      toast.error(submitError instanceof Error ? submitError.message : 'Failed to create agent');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleAgent = async (agent: AgentSummary) => {
    try {
      const response = await apiClient(`/api/admin/agents/${agent.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !agent.isActive }),
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as AgentSummary;
      setAgents((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(`${updated.displayName} ${updated.isActive ? 'enabled' : 'disabled'}`);
    } catch (toggleError) {
      console.error(toggleError);
      toast.error('Unable to update agent status');
    }
  };

  const handleDeleteAgent = async (agent: AgentSummary) => {
    const confirmed = window.confirm(`Remove agent "${agent.displayName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    try {
      const response = await apiClient(`/api/admin/agents/${agent.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      setAgents((prev) => prev.filter((item) => item.id !== agent.id));
      setIssuedSecrets((prev) => {
        const next = { ...prev };
        agent.tokens.forEach((token) => {
          delete next[token.id];
        });
        return next;
      });
      toast.success(`Removed agent ${agent.displayName}`);
    } catch (deleteError) {
      console.error(deleteError);
      toast.error('Unable to delete agent');
    }
  };

  const handleRevokeToken = async (token: AgentTokenSummary) => {
    try {
      const response = await apiClient(`/api/admin/agent-tokens/${token.id}/revoke`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      toast.success('Token revoked');
      await loadAgents();
    } catch (revokeError) {
      console.error(revokeError);
      toast.error('Unable to revoke token');
    }
  };

  const handleExtendToken = async (token: AgentTokenSummary) => {
    try {
      const response = await apiClient(`/api/admin/agent-tokens/${token.id}/extend`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const updated = (await response.json()) as AgentTokenSummary;
      toast.success('Token extended by 1 year');
      await loadAgents();
    } catch (extendError) {
      console.error(extendError);
      toast.error('Unable to extend token');
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Token copied to clipboard');
    } catch (copyError) {
      console.warn(copyError);
      toast.error('Unable to copy token');
    }
  };

  return (
    <div className="admin-section">
      <header className="admin-section-header">
        <div>
          <h1>Agents</h1>
          <p>Provision API credentials and QR codes for field agents.</p>
        </div>
      </header>

      <form className="admin-form" onSubmit={handleCreateAgent}>
        <h2>Create Agent</h2>
        <div className="admin-form-grid">
          <label>
            <span>Display Name</span>
            <input
              type="text"
              value={form.displayName}
              onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))}
              placeholder="Dr. Jane Doe"
              required
              disabled={isSubmitting}
            />
          </label>
          <label>
            <span>External Identifier</span>
            <input
              type="text"
              value={form.externalIdentifier}
              onChange={(event) => setForm((prev) => ({ ...prev, externalIdentifier: event.target.value }))}
              placeholder="Badge ID (optional)"
              disabled={isSubmitting}
            />
          </label>
        </div>
        <label>
          <span>Notes</span>
          <textarea
            value={form.notes}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            placeholder="Specialties, contact details, or deployment notes"
            disabled={isSubmitting}
          />
        </label>
        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
            disabled={isSubmitting}
          />
          <span>Active immediately</span>
        </label>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Add Agent'}
        </button>
      </form>

      <section className="admin-list">
        <h2>Agent Directory</h2>
        {isLoading ? <p>Loading agents…</p> : null}
        {error ? <p className="admin-error">{error}</p> : null}
        {!isLoading && !error ? (
          sortedAgents.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '760px',
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left', color: '#cbd5f5' }}>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Name</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Identifier</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Status</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Created</th>
                    <th style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(148,163,184,0.3)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAgents.map((agent) => {
                    const isExpanded = expandedAgent === agent.id;
                    const tokens = agent.tokens ?? [];
                    const defaultTokenId = tokens[0]?.id ?? null;
                    const isTokenExpanded = expandedTokenId && tokens.some((token) => token.id === expandedTokenId);
                    return (
                      <Fragment key={agent.id}>
                        <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                          <td style={{ padding: '0.85rem 1rem', color: '#e2e8f0', fontWeight: 600 }}>{agent.displayName}</td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>
                            {agent.externalIdentifier || '—'}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <span className={agent.isActive ? 'badge badge-success' : 'badge badge-neutral'}>
                              {agent.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '0.85rem 1rem', color: '#94a3b8' }}>
                            {new Date(agent.createdAt).toLocaleString()}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => handleToggleAgent(agent)}>
                                {agent.isActive ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAgent(agent)}
                                style={{
                                  border: '1px solid rgba(248, 113, 113, 0.4)',
                                  background: 'rgba(248, 113, 113, 0.15)',
                                  color: '#fecaca',
                                }}
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (isExpanded) {
                                    setExpandedAgent(null);
                                    setExpandedTokenId(null);
                                  } else {
                                    setExpandedAgent(agent.id);
                                    setExpandedTokenId(defaultTokenId);
                                  }
                                }}
                              >
                                {isExpanded ? 'Hide Details' : 'Show Details'}
                              </button>
                            </div>
                            <span style={{ display: 'none' }}>
                              {tokens.map((token) => {
                                const qrValue =
                                  issuedSecrets[token.id]?.qrPayload ?? token.qrPayload ?? '';
                                return (
                                  <QRCodeCanvas
                                    key={token.id}
                                    value={qrValue || ''}
                                    size={132}
                                    includeMargin
                                    id={`agent-qr-${token.id}`}
                                  />
                                );
                              })}
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={5} style={{ padding: '1rem 1.25rem', background: 'rgba(15,23,42,0.65)' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', color: '#cbd5f5' }}>
                                {agent.notes ? (
                                  <p style={{ margin: 0, color: '#94a3b8' }}>{agent.notes}</p>
                                ) : null}
                                <div>
                                  <h4 style={{ margin: '0 0 0.5rem', color: '#e2e8f0' }}>Token</h4>
                                  {tokens.length ? (
                                    tokens.map((token) => {
                                      const secret = issuedSecrets[token.id];
                                      const qrValue = secret?.qrPayload ?? token.qrPayload ?? '';
                                      const showTokenQr = expandedTokenId === token.id;
                                      return (
                                        <div
                                          key={token.id}
                                          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                                        >
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            <span className="badge badge-neutral">
                                              Prefix&nbsp;{token.tokenPrefix}
                                            </span>
                                            <span className="badge badge-neutral">
                                              Issued&nbsp;{new Date(token.createdAt).toLocaleString()}
                                            </span>
                                            <span className="badge badge-neutral">
                                              {token.expiresAt
                                                ? `Expires ${new Date(token.expiresAt).toLocaleString()}`
                                                : 'Never expires'}
                                            </span>
                                            <span className="badge badge-neutral">
                                              {token.revokedAt
                                                ? `Revoked ${new Date(token.revokedAt).toLocaleString()}`
                                                : 'Active'}
                                            </span>
                                          </div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {!token.revokedAt ? (
                                              <>
                                                <button type="button" onClick={() => void handleRevokeToken(token)}>
                                                  Revoke
                                                </button>
                                                <button type="button" onClick={() => void handleExtendToken(token)}>
                                                  Extend by 1 year
                                                </button>
                                              </>
                                            ) : null}
                                            {secret ? (
                                              <button type="button" onClick={() => void handleCopy(secret.token)}>
                                                Copy Secret
                                              </button>
                                            ) : null}
                                            {qrValue ? (
                                              <>
                                                <button
                                                  type="button"
                                                  onClick={() => handleDownloadQr(token.id, agent.displayName, token.tokenPrefix)}
                                                >
                                                  Download PNG
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setExpandedTokenId(showTokenQr ? null : token.id)
                                                  }
                                                >
                                                  {showTokenQr ? 'Hide QR' : 'Show QR'}
                                                </button>
                                              </>
                                            ) : null}
                                          </div>
                                          {showTokenQr && qrValue ? (
                                            <div className="admin-qr-wrapper">
                                              <QRCodeSVG value={qrValue} size={132} />
                                              {secret ? (
                                                <div>
                                                  <p>Token</p>
                                                  <code>{secret.token}</code>
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <p style={{ color: '#94a3b8' }}>No token available.</p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No agents registered yet.</p>
          )
        ) : null}
      </section>
    </div>
  );
}
